import { createSelector } from 'reselect';

import {
	AviaSearchParameters,
	CreateExchangeParams,
	ExchangePriceParams,
	GetExchangeVariantsParams,
	TravellerFieldEnum
} from '@websky/graphql';
import { API_DATE_FORMAT, format, getTravellerName, getUserValue } from '../../../../utils';

import {
	getOrderId,
	getOrderHash,
	getCustomer,
	getTravellers,
	getLastExchange
} from '../../../../Checkout/store/order/selectors';

import { AnyStateWithExchange } from './types';
import { getSelectedPassengers, getSelectedPassengersIds, getSelectedPassengersParams } from './passengers/selectors';
import {
	getSelectedSegments,
	getSelectedSegmentsIds,
	getSelectedSegmentsParams,
	getIsAllSegmentsSelected
} from './segments/selectors';
import {
	getSelectedFlightsCount,
	getExchangeTargetFlight,
	getExchangeTargetFlightActualPriceId
} from './exchangeVariants/selectors';
import { CREATE_EXCHANGE_LEG_INDEX } from '../lib/getExchangeTargetFlightIndex';

export const getExchangeState = (state: AnyStateWithExchange) => state.exchange;

export const getSearchParameters = createSelector(
	getSelectedPassengersParams,
	getSelectedSegmentsParams,
	(passengersParams, segmentsParams): AviaSearchParameters => {
		return {
			passengers: passengersParams,
			segments: segmentsParams
		};
	}
);

export const getExchangeVariantsParams = createSelector(
	getOrderId,
	getSelectedPassengersIds,
	getSelectedSegments,
	(orderId, selectedPassengersIds, selectedSegments): GetExchangeVariantsParams => ({
		orderId,
		passengers: selectedPassengersIds.map(Number),
		segmentParams: selectedSegments.map(segment => ({
			segmentGroupId: segment.id,
			date: format(segment.selectedDate ?? segment.date, API_DATE_FORMAT)
		})),
		isCompelled: false
	})
);

export const getSelectedSegmentOnLeg = createSelector(
	getSelectedSegments,
	getSelectedFlightsCount,
	(selectedSegments, selectedFlightsCount) => selectedSegments[selectedFlightsCount]
);

export const getSelectedSegmentMinDate = createSelector(
	getSelectedSegments,
	getSelectedFlightsCount,
	(selectedSegments, selectedFlightsCount) => {
		const prevSelectedSegment = selectedSegments[selectedFlightsCount - 1];

		if (!prevSelectedSegment) {
			return new Date(new Date().setHours(0, 0, 0, 0));
		}

		return prevSelectedSegment.selectedDate ?? prevSelectedSegment.date;
	}
);

export const getRefundServicesParams = createSelector(
	getOrderId,
	getLastExchange,
	getTravellers,
	getSelectedPassengersIds,
	getSelectedSegmentsIds,
	getIsAllSegmentsSelected,
	(orderId, lastExchange, passengers, selectedPassengerIds, selectedSegmentIds, allFlightToExchange) => {
		if (lastExchange) {
			return {
				orderId,
				passengers: [],
				selectedSegmentIds: [],
				selectedPassengerIds: [],
				allFlightToExchange: false
			};
		}

		return {
			orderId,
			passengers,
			selectedPassengerIds,
			selectedSegmentIds,
			allFlightToExchange
		};
	}
);

export const getInitialFormValues = createSelector(
	getCustomer,
	getSelectedPassengers,
	(customer, selectedPassengersValues) => {
		const { fullName, email, phone } = selectedPassengersValues[0]?.values ?? {};

		return {
			name: fullName || getTravellerName(customer),
			email: email || getUserValue(customer, TravellerFieldEnum.Email),
			phone: phone || getUserValue(customer, TravellerFieldEnum.Phone),
			comment: ''
		};
	}
);

export const getCreateExchangeParams = createSelector(
	getOrderId,
	getSelectedPassengersIds,
	getSelectedSegmentsIds,
	getExchangeTargetFlightActualPriceId,
	(orderId, selectedPassengersIds, selectedSegmentsIds, targetFlightActualPriceId): CreateExchangeParams => ({
		orderId,
		passengers: selectedPassengersIds.map(Number),
		segmentGroupId: Number(selectedSegmentsIds[CREATE_EXCHANGE_LEG_INDEX]),
		newFlightId: targetFlightActualPriceId,
		isCompelled: false,
		comment: ''
	})
);

export const getExchangePriceParameters = createSelector(
	getOrderId,
	getOrderHash,
	getExchangeTargetFlight,
	getSelectedPassengersIds,
	(orderId, orderStateHash, targetFlight, selectedPassengersIds): ExchangePriceParams => {
		return {
			orderId,
			orderStateHash,
			flightId: targetFlight?.priceId ?? '',
			passengers: selectedPassengersIds.map(Number)
		};
	}
);
